import { useRecoilCallback } from "recoil";

import {
  companyIdSelector,
  defaultReportPaginationState,
  getReportColumnOrderState,
  getReportColumnsSizingLocalStore,
  getReportVisibilityState,
  hasKeys,
  reportDataAF,
  reportFiltersWithValuesAF,
  reportFlexGroupingParamsAF,
  reportGroupingPeriodAF,
  ReportPeriodGroupingEnum,
  reportsFilterSettingsAF,
  reportsTableColumnsOrderStateAF,
  reportsTableColumnsSizingAF,
  reportsTableColumnsSortingAF,
  reportsTableColumnsVisibilityAF,
  reportTableItemPaginationAF,
  type IReportActionParams,
  type IReportApiDto,
  type IReportFiltersSchemaSettings,
} from "@ryde/shared";

export const useReportsActionsUtils = () => {
  const setReportData = useRecoilCallback(
    ({ set }) =>
      (curReportId: number, reportData: IReportApiDto) => {
        set(reportDataAF(curReportId), reportData);
      },
  );

  const clearReportData = useRecoilCallback(
    ({ reset }) =>
      (curReportId: number) => {
        reset(reportDataAF(curReportId));
      },
  );

  const setReportColumnVisibility = useRecoilCallback(
    ({ set, snapshot }) =>
      async ({
        reportId,
        reportApiDto,
        fetchCriteria,
        isSameReportRender,
      }: IReportActionParams) => {
        const nextVisibilityState = getReportVisibilityState({
          reportApiDto,
          fetchCriteria,
          isSameReportRender,
        });

        if (!isSameReportRender) {
          set(reportsTableColumnsVisibilityAF(reportId), nextVisibilityState);
        } else {
          const previousVisibilityState = await snapshot.getPromise(
            reportsTableColumnsVisibilityAF(reportId),
          );

          if (!hasKeys(previousVisibilityState)) {
            set(reportsTableColumnsVisibilityAF(reportId), nextVisibilityState);
          }
        }
      },
  );

  const setReportColumnOrderState = useRecoilCallback(
    ({ set, snapshot }) =>
      async ({
        reportId,
        reportApiDto,
        fetchCriteria,
        isSameReportRender,
      }: IReportActionParams) => {
        const nextColumnOrderState = getReportColumnOrderState({
          reportApiDto,
          fetchCriteria,
          isSameReportRender,
        });

        if (!isSameReportRender) {
          set(reportsTableColumnsOrderStateAF(reportId), nextColumnOrderState);
        } else {
          const previousOrderState = await snapshot.getPromise(
            reportsTableColumnsOrderStateAF(reportId),
          );

          if (!previousOrderState?.length) {
            set(
              reportsTableColumnsOrderStateAF(reportId),
              nextColumnOrderState,
            );
          }
        }
      },
  );

  const setReportSchemaValues = useRecoilCallback(
    ({ set }) =>
      (curReportId: number, reportApiDto: IReportApiDto) => {
        const visibleFilters = reportApiDto?.report?.visibleFilters;

        if (visibleFilters) {
          set(reportFiltersWithValuesAF(curReportId), visibleFilters);
        }
      },
  );

  const setReportFilterSettings = useRecoilCallback(
    ({ set }) =>
      (curReportId: number, reportApiDto: IReportApiDto) => {
        const schema = reportApiDto?.reportTemplate?.availableFilters || {};
        const filterSchemaSettingsComputed = Object.keys(schema).reduce(
          (acc, key) => {
            if (schema[key].dropdownSource) {
              acc[key] = {
                dropdownSource: schema[key].dropdownSource,
                selectItemsSource: schema[key].selectItemsSource,
              };
            }

            return acc;
          },
          {} as IReportFiltersSchemaSettings,
        );
        set(reportsFilterSettingsAF(curReportId), filterSchemaSettingsComputed);
      },
  );

  const setReportClientSortState = useRecoilCallback(
    ({ set, snapshot }) =>
      async (
        curReportId: number,
        reportApiDto: IReportApiDto,
        isSameReportRender: boolean,
      ) => {
        const { report } = reportApiDto;

        const { clientColumnSort } = report || {};

        if (clientColumnSort) {
          if (!isSameReportRender) {
            set(reportsTableColumnsSortingAF(curReportId), clientColumnSort);
          } else {
            const curOrderState = await snapshot.getPromise(
              reportsTableColumnsSortingAF(curReportId),
            );

            if (!curOrderState?.length) {
              set(reportsTableColumnsSortingAF(curReportId), clientColumnSort);
            }
          }
        }
      },
  );

  // Report widths stay in this browser only, so they come from local storage and not from the report dto.
  const setReportColumnsSizing = useRecoilCallback(
    ({ set, snapshot }) =>
      (curReportId: number) => {
        const companyId = snapshot.getLoadable(companyIdSelector).getValue();

        set(
          reportsTableColumnsSizingAF(curReportId),
          companyId
            ? getReportColumnsSizingLocalStore({
                companyId,
                reportId: curReportId,
              })
            : {},
        );
      },
  );

  const setReportPeriodGrouping = useRecoilCallback(
    ({ set }) =>
      (curReportId: number, reportApiDto: IReportApiDto) => {
        const { report } = reportApiDto;

        if (report.periodGrouping) {
          set(reportGroupingPeriodAF(curReportId), report.periodGrouping);
        } else {
          set(
            reportGroupingPeriodAF(curReportId),
            ReportPeriodGroupingEnum.EntirePeriod,
          );
        }
      },
  );

  const setReportFlexGroupingParams = useRecoilCallback(
    ({ set }) =>
      (curReportId: number, reportApiDto: IReportApiDto) => {
        const { report } = reportApiDto;

        if (report.flexGroupingColumnKeys) {
          set(
            reportFlexGroupingParamsAF(curReportId),
            report.flexGroupingColumnKeys,
          );
        } else {
          set(reportFlexGroupingParamsAF(curReportId), []);
        }
      },
  );

  const resetReportPagination = useRecoilCallback(
    ({ set }) =>
      (curReportId: number) => {
        set(
          reportTableItemPaginationAF(curReportId),
          defaultReportPaginationState,
        );
      },
  );

  return {
    setReportData,
    clearReportData,
    setReportFilterSettings,
    setReportColumnVisibility,
    setReportSchemaValues,
    setReportColumnOrderState,
    setReportClientSortState,
    setReportColumnsSizing,
    setReportPeriodGrouping,
    setReportFlexGroupingParams,
    resetReportPagination,
  };
};
